/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import * as React from "react";
import styled, { css, keyframes } from "styled-components";
import { AlertTriangle, Check, CircleAlert, Info, X } from "lucide-react";

import type { AlertInfoLevel } from "@src/@types/NotificationItem";

/*
 * The one toast of every Tech9IQ module, as the Ops Console draws it (admin/styles.css .toast and
 * #toast-host): a surface card in the bottom-right corner with a 3px edge and an icon tile in the
 * kind's colour, a title (what happened), an optional message (the detail), optional action links
 * and a close button. Success, info and warning close after 6 s; errors stay until closed. At most
 * three are shown, newest on top.
 */
export const TOAST_AUTO_DISMISS_MS = 6000;
export const MAX_TOASTS = 3;

export type ToastKind = AlertInfoLevel;
export type ToastAction = { label: string; onClick: () => void };
export type ToastData = {
  id: number;
  kind: ToastKind;
  title: string;
  message?: string;
  actions?: ToastAction[];
};

const KIND = {
  success: { edge: "var(--t9-success-700)", bg: "var(--t9-success-50)", Icon: Check },
  error: { edge: "var(--t9-danger-700)", bg: "var(--t9-danger-50)", Icon: CircleAlert },
  warning: { edge: "var(--t9-warning-700)", bg: "var(--t9-warning-50)", Icon: AlertTriangle },
  info: { edge: "var(--t9-info-700)", bg: "var(--t9-info-50)", Icon: Info },
};

const toastIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: none; }
`;

const Host = styled.div`
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: min(380px, calc(100vw - 32px));
  pointer-events: none;
  @media (max-width: 720px) {
    right: 16px;
    bottom: 16px;
  }
`;
const Card = styled.div<{ kind: ToastKind; leaving: boolean }>`
  pointer-events: auto;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  background: var(--t9-bg-raised);
  border: 1px solid var(--t9-border);
  border-left: 3px solid ${p => KIND[p.kind].edge};
  border-radius: 8px;
  box-shadow: var(--t9-shadow-lg);
  font-family: Montserrat, ui-sans-serif, system-ui, "Segoe UI", sans-serif;
  animation: ${toastIn} 0.2s ease;
  transition:
    opacity 180ms ease-in,
    transform 180ms ease-in;
  ${p =>
    p.leaving
      ? css`
          opacity: 0;
          transform: translateY(4px);
        `
      : ""}
  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;
const IconTile = styled.div<{ kind: ToastKind }>`
  width: 24px;
  height: 24px;
  margin-top: 1px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  border-radius: 6px;
  background: ${p => KIND[p.kind].bg};
  color: ${p => KIND[p.kind].edge};
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
`;
const Title = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: var(--t9-text);
  overflow-wrap: anywhere;
`;
const Message = styled.div`
  margin-top: 2px;
  font-size: 12px;
  line-height: 16px;
  color: var(--t9-text-secondary);
  overflow-wrap: anywhere;
  white-space: pre-line;
`;
const Actions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 6px;
`;
const ActionLink = styled.button`
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: var(--t9-link);
  cursor: pointer;
  &:hover {
    text-decoration: underline;
  }
  &:focus-visible {
    outline: 2px solid var(--t9-focus);
    outline-offset: 2px;
    border-radius: 2px;
  }
`;
const Close = styled.button`
  flex-shrink: 0;
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: none;
  color: var(--t9-text-faint);
  opacity: 0.8;
  cursor: pointer;
  &:hover {
    opacity: 1;
    color: var(--t9-text);
  }
  &:focus-visible {
    outline: 2px solid var(--t9-focus);
    outline-offset: 1px;
  }
`;

type ItemProps = { toast: ToastData; onClose: (id: number) => void };

export const Toast = ({ toast, onClose }: ItemProps) => {
  const [leaving, setLeaving] = React.useState(false);
  const close = React.useCallback(() => {
    setLeaving(true);
    window.setTimeout(() => onClose(toast.id), 200);
  }, [onClose, toast.id]);

  React.useEffect(() => {
    if (toast.kind === "error") return undefined;
    const timer = window.setTimeout(close, TOAST_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [close, toast.kind]);

  const { Icon } = KIND[toast.kind];
  return (
    <Card
      kind={toast.kind}
      leaving={leaving}
      role={toast.kind === "error" ? "alert" : "status"}
      data-toast-kind={toast.kind}
    >
      <IconTile kind={toast.kind} aria-hidden="true">
        <Icon size={14} strokeWidth={2.2} />
      </IconTile>
      <Body>
        <Title>{toast.title}</Title>
        {toast.message ? <Message>{toast.message}</Message> : null}
        {toast.actions && toast.actions.length ? (
          <Actions>
            {toast.actions.map(a => (
              <ActionLink key={a.label} type="button" onClick={a.onClick}>
                {a.label}
              </ActionLink>
            ))}
          </Actions>
        ) : null}
      </Body>
      <Close type="button" aria-label="Dismiss" title="Dismiss" onClick={close}>
        <X size={14} strokeWidth={2} aria-hidden="true" />
      </Close>
    </Card>
  );
};

type HostProps = { toasts: ToastData[]; onClose: (id: number) => void };

/** The stack: newest on top, at most MAX_TOASTS. */
export const ToastHost = ({ toasts, onClose }: HostProps) => (
  <Host aria-live="polite">
    {toasts
      .slice(-MAX_TOASTS)
      .reverse()
      .map(t => (
        <Toast key={t.id} toast={t} onClose={onClose} />
      ))}
  </Host>
);

export default ToastHost;
